/**
 * Discovery Orchestrator Page
 *
 * Natural language interface for discovering products, personas,
 * content opportunities, and exploring the knowledge base.
 */
import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Send, Loader2, Sparkles, Package, Users, HelpCircle, BarChart3, RefreshCw, X, ExternalLink, Star, Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { useSite } from '@/contexts/SiteContext';
import { authFetch, handleResponse } from '@/utils/auth-fetch';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// Product Card Component with image, price, specs, and match info
interface ProductCardProps {
  product: {
    id: string;
    title: string;
    image?: string;
    price?: number | string;
    currency?: string;
    url?: string;
    similarity?: number;
    benefitScore?: number;
    matchReason?: string;
    specs?: Record<string, string>;
    isAEO?: boolean;
    aeoSection?: string;
  };
}

function ProductCard({ product }: ProductCardProps) {
  // Calculate match percentage (use similarity or benefitScore)
  const matchScore = product.similarity || product.benefitScore;
  const matchPercent = matchScore ? Math.round(matchScore * 100) : null;

  // Default placeholder image
  const imageUrl = product.image || 'https://placehold.co/120x120/e2e8f0/94a3b8?text=No+Image';

  return (
    <div className="flex gap-3 p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 hover:shadow-md transition-shadow">
      {/* Product Image */}
      <div className="flex-shrink-0">
        <img
          src={imageUrl}
          alt={product.title}
          className="w-20 h-20 object-cover rounded-md bg-slate-100"
          onError={(e) => {
            (e.target as HTMLImageElement).src = 'https://placehold.co/120x120/e2e8f0/94a3b8?text=No+Image';
          }}
        />
      </div>

      {/* Product Info */}
      <div className="flex-1 min-w-0">
        {/* Title - no truncation */}
        <h4 className="font-medium text-sm text-foreground leading-tight mb-1">
          {product.title}
        </h4>

        {/* Price */}
        {product.price && (
          <div className="text-base font-bold text-primary mb-1">
            {typeof product.price === 'number'
              ? `${product.currency || '€'}${product.price.toFixed(2)}`
              : product.price}
          </div>
        )}

        {/* Key Specs (if available) */}
        {product.specs && Object.keys(product.specs).length > 0 && (
          <div className="flex flex-wrap gap-1 mb-1">
            {Object.entries(product.specs).slice(0, 3).map(([key, value]) => (
              <span key={key} className="text-xs bg-slate-100 dark:bg-slate-700 px-1.5 py-0.5 rounded">
                {key}: {value}
              </span>
            ))}
          </div>
        )}

        {/* Match Reason */}
        {product.matchReason && (
          <p className="text-xs text-muted-foreground italic mb-1">
            {product.matchReason}
          </p>
        )}

        {/* Match Score Badge */}
        <div className="flex items-center gap-2">
          {matchPercent && matchPercent > 0 && (
            <Badge
              variant="secondary"
              className={`text-xs ${
                matchPercent >= 80 ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                matchPercent >= 50 ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-200'
              }`}
            >
              <Star className="w-3 h-3 mr-1" />
              {matchPercent}% match
            </Badge>
          )}
          {product.isAEO && (
            <Badge variant="outline" className="text-xs">
              AEO
            </Badge>
          )}
          {product.url && (
            <a
              href={product.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-primary hover:underline flex items-center gap-1"
            >
              View <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

// Types
interface ProductItem {
  id: string;
  title: string;
  image?: string;
  price?: number | string;
  currency?: string;
  url?: string;
  similarity?: number;
  benefitScore?: number;
  matchReason?: string;
  specs?: Record<string, string>;
  isAEO?: boolean;
  aeoSection?: string;
}

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  hidden?: boolean; // For auto-prompt messages we don't want to show
  displayContent?: string; // Simplified content to show instead
  metadata?: {
    intent?: string;
    confidence?: number;
    sources?: Array<{ type: string; count: number }>;
    suggestions?: Array<{ text: string; intent: string }>;
  };
  data?: {
    type: string;
    items?: ProductItem[];
    matchingProducts?: ProductItem[];
    data?: any;
  } | null;
  createdAt: string;
}

interface Session {
  sessionId: string;
  siteId: string;
  siteName: string;
  createdAt: string;
}

interface Capability {
  id: string;
  name: string;
  description: string;
  available: boolean;
  count: number | null;
}

interface Starter {
  id: string;
  type: string;
  title: string;
  description: string;
  priorityScore: number;
  payload: any;
}

// Intent icon mapping
const intentIcons: Record<string, React.ReactNode> = {
  product_search: <Package className="h-4 w-4" />,
  persona_exploration: <Users className="h-4 w-4" />,
  question_discovery: <HelpCircle className="h-4 w-4" />,
  knowledge_discovery: <BarChart3 className="h-4 w-4" />,
  default: <Sparkles className="h-4 w-4" />,
};

export default function DiscoveryPage() {
  const { selectedSite } = useSite();
  const location = useLocation();
  const navigate = useNavigate();
  const [session, setSession] = useState<Session | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const [capabilities, setCapabilities] = useState<Capability[]>([]);
  const [starters, setStarters] = useState<Starter[]>([]);
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  const [pendingAutoPrompt, setPendingAutoPrompt] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const lastUserMessageRef = useRef<HTMLDivElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  // Handle incoming auto-prompt from Conversation Intelligence
  useEffect(() => {
    const state = location.state as { autoPrompt?: string; source?: string; platform?: string; conversationUrl?: string } | null;
    if (state?.autoPrompt && selectedSite?.id) {
      setPendingAutoPrompt(state.autoPrompt);
      // Clear the location state to prevent re-triggering on navigation
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, selectedSite?.id]);

  // Auto-start session and send message when we have a pending prompt
  useEffect(() => {
    if (pendingAutoPrompt && selectedSite?.id && !session && !initializing) {
      const autoStart = async () => {
        await startSession();
      };
      autoStart();
    }
  }, [pendingAutoPrompt, selectedSite?.id, session, initializing]);

  // Send the pending prompt once session is ready
  useEffect(() => {
    if (pendingAutoPrompt && session && !loading) {
      const prompt = pendingAutoPrompt;
      setPendingAutoPrompt(null);

      // Extract the question or conversation title for display
      const questionMatch = prompt.match(/\*\*Question:\*\*\s*"([^"]+)"/) ||
                           prompt.match(/\*\*Conversation Title:\*\*\s*"([^"]+)"/);
      const question = questionMatch ? questionMatch[1] : 'Generating reply...';
      const displayContent = `Generating reply for: "${question.substring(0, 100)}${question.length > 100 ? '...' : ''}"`;

      // Small delay to ensure session is fully ready
      setTimeout(() => sendMessage(prompt, { hidden: true, displayContent }), 500);
    }
  }, [pendingAutoPrompt, session, loading]);

  // Clean status messages and intros from content
  const cleanReplyContent = (content: string): string => {
    let cleaned = content;

    // Extract content between --- dividers if present (the actual reply)
    const dividerPattern = /^---\s*$/gm;
    const parts = cleaned.split(dividerPattern);

    if (parts.length >= 3) {
      cleaned = parts[1].trim();
    } else if (parts.length === 2) {
      cleaned = parts[0].length > parts[1].length ? parts[0].trim() : parts[1].trim();
    }

    // Remove status/progress messages at the start (can span multiple lines)
    const statusPatterns = [
      /^Checking[\s\S]*?\.{3}\s*/i,  // "Checking customer questions..."
      /^Searching[\s\S]*?\.{3}\s*/i,
      /^Looking up[\s\S]*?\.{3}\s*/i,
      /^Analyzing[\s\S]*?\.{3}\s*/i,
      /^Let me (check|search|look|find)[\s\S]*?\.{3}\s*/i,
      /^[Cc]ustomer questions\.{3}\s*/,  // Leftover from multi-line "Checking\ncustomer questions..."
      // Spanish status messages
      /^Déjame ver[\s\S]*?\.{3}\s*/i,  // "Déjame ver qué preguntas..."
      /^Buscando[\s\S]*?\.{3}\s*/i,
      /^Analizando[\s\S]*?\.{3}\s*/i,
      /^Consultando[\s\S]*?\.{3}\s*/i,
      /^Revisando[\s\S]*?\.{3}\s*/i,
      /^Voy a (buscar|revisar|consultar)[\s\S]*?\.{3}\s*/i,
    ];
    for (const pattern of statusPatterns) {
      cleaned = cleaned.replace(pattern, '');
    }

    // Remove conversational intros
    const introPatterns = [
      /^(Of course|Sure|Absolutely|Great question|Happy to help)[!,.]?\s*(Let me see what I can help with\.{0,3})?\s*/i,
      /^(Here's|Here is) (a |my )?(draft |suggested )?(response|reply|answer).*?:\s*/i,
      /^I'd be happy to help[^.]*\.\s*/i,
      // Spanish intros
      /^(Claro|Por supuesto|Aquí tienes|Con gusto)[!,.]?\s*/i,
      /^(Aquí está|Te comparto) (una |mi )?(respuesta|sugerencia).*?:\s*/i,
    ];
    for (const pattern of introPatterns) {
      cleaned = cleaned.replace(pattern, '');
    }

    // Handle "Absolutely, when it comes to X" -> "When it comes to X"
    cleaned = cleaned.replace(/^(Absolutely|Sure)[,!]?\s+(when it comes to)/i, '$2');

    return cleaned.trim();
  };

  // Copy message content to clipboard, extracting just the core reply
  const copyToClipboard = async (messageId: string, content: string) => {
    try {
      let textToCopy = cleanReplyContent(content);

      // Capitalize the first letter if we stripped an intro
      if (textToCopy.length > 0 && textToCopy[0] === textToCopy[0].toLowerCase()) {
        textToCopy = textToCopy[0].toUpperCase() + textToCopy.slice(1);
      }

      // Remove outro/closing meta-comments
      const outroPatterns = [
        /\n\s*(Feel free to|Hope this helps|Let me know if|I hope these?|Ultimately,? it'?s all about).*$/is,
        /\n\s*Happy (riding|cycling|biking)!?\s*$/i,
      ];
      for (const pattern of outroPatterns) {
        textToCopy = textToCopy.replace(pattern, '');
      }

      await navigator.clipboard.writeText(textToCopy.trim());
      setCopiedMessageId(messageId);
      setTimeout(() => setCopiedMessageId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Focus input when session starts or after sending a message
  useEffect(() => {
    if (session && !loading) {
      // Small delay to ensure DOM is ready
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [session, loading]);

  // Scroll to position the user's message at the top of the viewport
  const scrollToLastUserMessage = () => {
    setTimeout(() => {
      if (lastUserMessageRef.current && scrollAreaRef.current) {
        // Find the ScrollArea viewport (it's the element with data-radix-scroll-area-viewport)
        const viewport = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
        if (viewport) {
          // Calculate position: message offset from top of scroll content
          const messageTop = lastUserMessageRef.current.offsetTop;
          // Scroll so the message is at the top with a small margin
          viewport.scrollTo({ top: messageTop - 16, behavior: 'smooth' });
        }
      }
    }, 150);
  };

  // Load capabilities and starters when site changes
  useEffect(() => {
    if (selectedSite?.id) {
      loadCapabilities();
      loadStarters();
    }
  }, [selectedSite?.id]);

  const loadCapabilities = async () => {
    if (!selectedSite?.id) return;
    try {
      const response = await authFetch(`/discovery/capabilities?siteId=${selectedSite.id}`);
      const data = await handleResponse<{ capabilities: Capability[] }>(response);
      setCapabilities(data.capabilities);
    } catch (error) {
      console.error('Failed to load capabilities:', error);
    }
  };

  const loadStarters = async () => {
    if (!selectedSite?.id) return;
    try {
      const response = await authFetch(`/discovery/starters?siteId=${selectedSite.id}`);
      const data = await handleResponse<{ starters: Starter[] }>(response);
      setStarters(data.starters);
    } catch (error) {
      console.error('Failed to load starters:', error);
    }
  };

  const startSession = async () => {
    if (!selectedSite?.id) return;
    setInitializing(true);
    try {
      const response = await authFetch('/discovery/sessions', {
        method: 'POST',
        body: JSON.stringify({ siteId: selectedSite.id }),
      });
      const data = await handleResponse<Session>(response);
      setSession(data);
      setMessages([]);

      // Add welcome message
      const welcomeMessage: Message = {
        id: `welcome-${Date.now()}`,
        role: 'assistant',
        content: `Hi! I'm your Discovery Assistant for **${data.siteName}**. I can help you:\n\n` +
          `- **Find products** by description, attributes, or criteria\n` +
          `- **Explore personas** and their preferences\n` +
          `- **Discover questions** customers are asking\n` +
          `- **Compare products** across different dimensions\n` +
          `- **Generate content briefs** from your knowledge base\n\n` +
          `What would you like to explore?`,
        createdAt: new Date().toISOString(),
      };
      setMessages([welcomeMessage]);
    } catch (error) {
      console.error('Failed to start session:', error);
    } finally {
      setInitializing(false);
    }
  };

  const sendMessage = async (messageText?: string, options?: { hidden?: boolean; displayContent?: string }) => {
    const text = messageText || input.trim();
    if (!text || !session) return;

    const userMessage: Message = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: text,
      hidden: options?.hidden,
      displayContent: options?.displayContent,
      createdAt: new Date().toISOString(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    // Scroll so the user's message appears at the top of the view
    scrollToLastUserMessage();

    // Create placeholder for streaming assistant message
    const assistantMessageId = `assistant-${Date.now()}`;
    const streamingMessage: Message = {
      id: assistantMessageId,
      role: 'assistant',
      content: '',
      createdAt: new Date().toISOString(),
    };
    setMessages(prev => [...prev, streamingMessage]);

    try {
      const token = localStorage.getItem('jwt') || localStorage.getItem('auth_token');

      // Use XMLHttpRequest to bypass OpenReplay's fetch interception
      // which causes issues with ReadableStream
      const responseText = await new Promise<string>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `/api/discovery/sessions/${session.sessionId}/message/stream`, true);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.setRequestHeader('Content-Type', 'application/json');

        let fullContent = '';
        let metadata: Message['metadata'] = {};
        let messageData: Message['data'] = null;
        let lastProcessedIndex = 0;

        xhr.onprogress = () => {
          // Process new data as it arrives
          const newData = xhr.responseText.substring(lastProcessedIndex);
          lastProcessedIndex = xhr.responseText.length;

          const lines = newData.split('\n');
          for (const line of lines) {
            if (!line || !line.startsWith('data: ')) continue;

            const jsonStr = line.slice(6);
            if (!jsonStr || jsonStr.trim() === '') continue;

            try {
              const eventData = JSON.parse(jsonStr);

              switch (eventData.type) {
                case 'meta':
                  metadata = {
                    intent: eventData.intent,
                    confidence: eventData.confidence,
                    sources: [],
                    suggestions: [],
                  };
                  setMessages(prev => prev.map(msg =>
                    msg.id === assistantMessageId
                      ? { ...msg, metadata: { ...metadata } }
                      : msg
                  ));
                  break;

                case 'token':
                  if (eventData.content) {
                    fullContent += eventData.content;
                    const currentContent = fullContent;
                    setMessages(prev => prev.map(msg =>
                      msg.id === assistantMessageId
                        ? { ...msg, content: currentContent }
                        : msg
                    ));
                  }
                  break;

                case 'complete':
                  metadata = {
                    ...metadata,
                    sources: Array.isArray(eventData.sources) ? eventData.sources : [],
                    suggestions: Array.isArray(eventData.suggestions) ? eventData.suggestions : [],
                  };
                  messageData = eventData.data ?? null;
                  // Final update with all data
                  const finalContent = fullContent;
                  const finalMetadata = { ...metadata };
                  const finalData = messageData;
                  setMessages(prev => prev.map(msg =>
                    msg.id === assistantMessageId
                      ? { ...msg, content: finalContent, metadata: finalMetadata, data: finalData }
                      : msg
                  ));
                  break;

                case 'error':
                  fullContent = eventData.message || eventData.error || 'An error occurred';
                  setMessages(prev => prev.map(msg =>
                    msg.id === assistantMessageId
                      ? { ...msg, content: fullContent }
                      : msg
                  ));
                  break;
              }
            } catch {
              // Ignore parse errors for partial lines
            }
          }
        };

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(xhr.responseText);
          } else {
            reject(new Error(`HTTP error! status: ${xhr.status}`));
          }
        };

        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send(JSON.stringify({ message: text }));
      });

    } catch (error: any) {
      console.error('[Discovery] Stream error:', error);
      setMessages(prev => prev.map(msg =>
        msg.id === assistantMessageId
          ? { ...msg, content: `I'm having trouble processing your request: ${error.message}. Please try again.` }
          : msg
      ));
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestionClick = (suggestion: { text: string; intent: string }) => {
    sendMessage(suggestion.text);
  };

  const handleStarterClick = async (starter: Starter) => {
    // Mark starter as acted upon
    try {
      await authFetch(`/discovery/starters/${starter.id}/act`, { method: 'POST' });
    } catch (e) {
      // Ignore error
    }

    // Start session if needed and send the starter text
    if (!session) {
      await startSession();
    }
    sendMessage(starter.title);
  };

  const dismissStarter = async (starterId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await authFetch(`/discovery/starters/${starterId}/dismiss`, { method: 'POST' });
      setStarters(prev => prev.filter(s => s.id !== starterId));
    } catch (error) {
      console.error('Failed to dismiss starter:', error);
    }
  };

  const endSession = () => {
    setSession(null);
    setMessages([]);
  };

  // Render data cards based on type
  const renderDataCard = (data: Message['data']) => {
    if (!data) return null;

    switch (data.type) {
      case 'product_list':
        const hasProducts = data.items && data.items.length > 0;
        const noProductsFound = (data as any).noProductsFound;

        return (
          <>
            <Card className="mt-3 border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-950/50">
              <CardHeader className="py-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Package className="h-4 w-4" />
                  {noProductsFound ? 'Related Information' : `Products Found (${data.items?.length || 0})`}
                </CardTitle>
                {noProductsFound && (
                  <CardDescription className="text-xs">
                    No exact product matches, but here's related content from our knowledge base
                  </CardDescription>
                )}
              </CardHeader>
              <CardContent className="py-2">
                {hasProducts ? (
                  <div className="space-y-3">
                    {data.items?.slice(0, 6).map((item: ProductItem, idx: number) => (
                      <ProductCard key={item.id || idx} product={item} />
                    ))}
                    {(data.items?.length || 0) > 6 && (
                      <div className="text-sm text-muted-foreground text-center py-2 border-t">
                        +{(data.items?.length || 0) - 6} more products available
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-sm text-muted-foreground text-center py-4">
                    No products found matching your criteria
                  </div>
                )}
              </CardContent>
            </Card>
            {/* Supplementary AEO info */}
            {(data as any).supplementaryInfo && (data as any).supplementaryInfo.length > 0 && (
              <Card className="mt-2 border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-900/50">
                <CardHeader className="py-2">
                  <CardTitle className="text-xs text-muted-foreground">Related Tips</CardTitle>
                </CardHeader>
                <CardContent className="py-2">
                  <div className="space-y-2">
                    {(data as any).supplementaryInfo.map((info: any, idx: number) => (
                      <div key={idx} className="text-xs text-muted-foreground">
                        <span className="font-medium">{info.title}</span>
                        {info.content && <p className="mt-0.5 opacity-80">{info.content}</p>}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        );

      case 'persona_list':
        return (
          <>
            <Card className="mt-3 border-purple-200 dark:border-purple-800 bg-purple-50/50 dark:bg-purple-950/50">
              <CardHeader className="py-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Personas ({data.items?.length || 0})
                </CardTitle>
              </CardHeader>
              <CardContent className="py-2">
                <div className="space-y-2">
                  {data.items?.map((item: any, idx: number) => (
                    <div key={item.id || idx} className="p-2 bg-white dark:bg-slate-800 rounded">
                      <div className="font-medium text-sm text-foreground">{item.name}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {item.description?.substring(0, 100)}...
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            {/* Show matching products if available */}
            {data.matchingProducts && data.matchingProducts.length > 0 && (
              <Card className="mt-3 border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-950/50">
                <CardHeader className="py-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Package className="h-4 w-4" />
                    Matching Products ({data.matchingProducts.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="py-2">
                  <div className="space-y-3">
                    {data.matchingProducts.slice(0, 6).map((item: ProductItem, idx: number) => (
                      <ProductCard key={item.id || idx} product={item} />
                    ))}
                    {data.matchingProducts.length > 6 && (
                      <div className="text-sm text-muted-foreground text-center py-2 border-t">
                        +{data.matchingProducts.length - 6} more products available
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        );

      case 'question_list':
        return (
          <Card className="mt-3 border-green-200 dark:border-green-800 bg-green-50/50 dark:bg-green-950/50">
            <CardHeader className="py-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <HelpCircle className="h-4 w-4" />
                Questions ({data.items?.length || 0})
              </CardTitle>
            </CardHeader>
            <CardContent className="py-2">
              <div className="space-y-2">
                {data.items?.slice(0, 5).map((item: any, idx: number) => (
                  <div key={item.id || idx} className="flex items-center justify-between text-sm p-2 bg-white dark:bg-slate-800 rounded">
                    <span className="truncate flex-1 text-foreground">"{item.text}"</span>
                    {item.volume && (
                      <Badge variant="outline" className="ml-2">
                        {item.volume.toLocaleString()} searches
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        );

      case 'stats':
        return (
          <Card className="mt-3 border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-950/50">
            <CardHeader className="py-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <BarChart3 className="h-4 w-4" />
                Knowledge Base Statistics
              </CardTitle>
            </CardHeader>
            <CardContent className="py-2">
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(data.data || {}).map(([key, value]: [string, any]) => (
                  <div key={key} className="p-2 bg-white dark:bg-slate-800 rounded text-center">
                    <div className="text-lg font-bold text-foreground">
                      {typeof value === 'object' ? value.total || '-' : value}
                    </div>
                    <div className="text-xs text-muted-foreground capitalize">
                      {key.replace(/_/g, ' ')}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        );

      default:
        return null;
    }
  };

  // No site selected
  if (!selectedSite) {
    return (
      <div className="container mx-auto p-6">
        <Card className="max-w-lg mx-auto">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5" />
              Discovery
            </CardTitle>
            <CardDescription>
              Please select a site from the header to start discovering.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  // No active session - show welcome screen
  if (!session) {
    return (
      <div className="container mx-auto p-6 max-w-4xl">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold flex items-center justify-center gap-3 mb-2">
            <Sparkles className="h-8 w-8 text-primary" />
            Discovery
          </h1>
          <p className="text-muted-foreground">
            Explore your product catalog, personas, and content opportunities using natural language
          </p>
        </div>

        {/* Capabilities */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold mb-4">What I Can Help With</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {capabilities.map(cap => (
              <Card
                key={cap.id}
                className={`${cap.available ? 'border-green-200' : 'border-gray-200 opacity-60'}`}
              >
                <CardHeader className="py-4">
                  <CardTitle className="text-sm flex items-center gap-2">
                    {intentIcons[cap.id] || intentIcons.default}
                    {cap.name}
                  </CardTitle>
                  <CardDescription className="text-xs">
                    {cap.description}
                  </CardDescription>
                </CardHeader>
                {cap.count !== null && (
                  <CardContent className="pt-0 pb-4">
                    <Badge variant={cap.available ? 'default' : 'secondary'}>
                      {cap.count} items
                    </Badge>
                  </CardContent>
                )}
              </Card>
            ))}
          </div>
        </div>

        {/* Conversation Starters */}
        {starters.length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-semibold mb-4">Suggested Starting Points</h2>
            <div className="space-y-3">
              {starters.slice(0, 5).map(starter => (
                <Card
                  key={starter.id}
                  className="cursor-pointer hover:border-primary transition-colors group"
                  onClick={() => handleStarterClick(starter)}
                >
                  <CardContent className="py-4 flex items-center justify-between">
                    <div>
                      <div className="font-medium">{starter.title}</div>
                      <div className="text-sm text-muted-foreground">{starter.description}</div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="opacity-0 group-hover:opacity-100"
                      onClick={(e) => dismissStarter(starter.id, e)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Start Button */}
        <div className="text-center">
          <Button
            size="lg"
            onClick={startSession}
            disabled={initializing}
            className="gap-2"
          >
            {initializing ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                Starting...
              </>
            ) : (
              <>
                <Sparkles className="h-5 w-5" />
                Start Discovery Session
              </>
            )}
          </Button>
        </div>
      </div>
    );
  }

  // Active session - show chat interface
  return (
    <div className="container mx-auto p-6 h-[calc(100vh-6rem)] flex flex-col max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 pb-4 border-b">
        <div className="flex items-center gap-3">
          <Sparkles className="h-6 w-6 text-primary" />
          <div>
            <h1 className="font-semibold">Discovery</h1>
            <p className="text-sm text-muted-foreground">{selectedSite.name}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={loadCapabilities}>
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={endSession}>
            New Session
          </Button>
        </div>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 pr-4" ref={scrollAreaRef}>
        <div className="space-y-4 pb-4">
          {messages.map((message, index) => {
            // Find if this is the last user message (for scroll targeting)
            const isLastUserMessage = message.role === 'user' &&
              messages.slice(index + 1).every(m => m.role !== 'user');

            return (
            <div
              key={message.id}
              ref={isLastUserMessage ? lastUserMessageRef : undefined}
              className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] rounded-lg p-4 ${
                  message.role === 'user'
                    ? message.hidden ? 'bg-primary/70 text-white/90' : 'bg-primary text-white'
                    : 'bg-muted'
                }`}
              >
                {/* Intent badge for assistant messages - hidden from shopper view */}
                {/* Confidence scores are internal metrics, not shown to users */}

                {/* Message content - show displayContent for hidden messages */}
                <div className={`prose prose-sm max-w-none ${
                  message.role === 'user'
                    ? 'prose-invert'
                    : 'dark:prose-invert'
                }`}>
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      table: ({ children }) => (
                        <div className="my-4 overflow-x-auto rounded-lg border border-slate-600 dark:border-slate-500">
                          <table className="w-full border-collapse text-sm">
                            {children}
                          </table>
                        </div>
                      ),
                      thead: ({ children }) => (
                        <thead className="bg-slate-700/50 dark:bg-slate-600/50">{children}</thead>
                      ),
                      th: ({ children }) => (
                        <th className="border-b border-r border-slate-600 dark:border-slate-500 px-4 py-2.5 text-left font-semibold last:border-r-0">
                          {children}
                        </th>
                      ),
                      td: ({ children }) => (
                        <td className="border-b border-r border-slate-700 dark:border-slate-600 px-4 py-2.5 last:border-r-0">
                          {children}
                        </td>
                      ),
                      tr: ({ children }) => (
                        <tr className="hover:bg-slate-700/30 dark:hover:bg-slate-600/30 last:border-b-0 [&>td]:last:border-b-0">{children}</tr>
                      ),
                    }}
                  >
                    {message.hidden && message.displayContent
                      ? message.displayContent
                      : (message.role === 'assistant' && !message.id.startsWith('welcome-')
                        ? cleanReplyContent(message.content)
                        : message.content)}
                  </ReactMarkdown>
                </div>

                {/* Copy button for assistant messages (not welcome message) */}
                {message.role === 'assistant' && message.content && !message.id.startsWith('welcome-') && (
                  <div className="mt-2 flex justify-end">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 gap-1.5 text-muted-foreground hover:text-foreground"
                      onClick={() => copyToClipboard(message.id, message.content)}
                    >
                      {copiedMessageId === message.id ? (
                        <>
                          <Check className="h-3.5 w-3.5 text-green-500" />
                          <span className="text-xs text-green-500">Copied!</span>
                        </>
                      ) : (
                        <>
                          <Copy className="h-3.5 w-3.5" />
                          <span className="text-xs">Copy reply</span>
                        </>
                      )}
                    </Button>
                  </div>
                )}

                {/* Structured data card */}
                {message.data && renderDataCard(message.data)}

                {/* Sources - hidden from shopper view (internal metric) */}

                {/* Suggestions */}
                {message.metadata?.suggestions && message.metadata.suggestions.length > 0 && (
                  <div className="mt-3 pt-3 border-t">
                    <div className="text-xs text-muted-foreground mb-2">Suggested follow-ups:</div>
                    <div className="flex flex-wrap gap-2">
                      {message.metadata.suggestions.map((suggestion, idx) => (
                        <Button
                          key={idx}
                          variant="outline"
                          size="sm"
                          className="text-xs h-auto py-1"
                          onClick={() => handleSuggestionClick(suggestion)}
                        >
                          {suggestion.text}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
          })}

          {/* Loading indicator */}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-muted rounded-lg p-4">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Input */}
      <div className="pt-4 border-t">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            sendMessage();
          }}
          className="flex gap-2"
        >
          <Input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about products, personas, questions..."
            disabled={loading}
            className="flex-1"
            autoFocus
          />
          <Button type="submit" disabled={loading || !input.trim()}>
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
